import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import i18n from "../../../i18n";
import { SelectListDto } from "../../../models";
import { TableSpinner } from "../../../components";
import { CourseEnum, RoleEnum } from "../../../enums";
import { State, UserProfileDto } from "../../../interfaces";
import DeleteModal from "../../../components/Common/DeleteModal";
import { BreadCrumb, NoResultFound } from "../../../components/Common";
import {
  deletePlan,
  deletePlanConsent,
  getCourseSelectList,
  getGradeSelectListByCourse,
  getPlans,
  toggleAddEditPlanModal,
  togglePlanState,
} from "../../../store/actions";
import { Plan } from "./Plan";
import { AddEditPlanModal } from ".";
import { PlanDto } from "../../../interfaces/plan/plan.dto";

type TProps = {};
export const PlansTabPage: React.FC<TProps> = () => {
  //#region hooks
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  //#endregion

  //#region redux state
  const { response: courseSelectList } = useSelector(
    (state: any): State<SelectListDto[]> => state.course.courseSelectList
  );

  const {
    response: plans,
    loading: loadingPlans,
    error: loadingPlansError,
  } = useSelector((state: any): State<PlanDto[]> => state.plan.plans);

  const {
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
  } = useSelector((state: any): State<any> => state.plan.deletePlan);

  const { response: grades, loading: loadingGrades } = useSelector(
    (state: any): State<SelectListDto[]> => state.grade.gradeSelectListByCourse
  );

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region internal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [course, setCourse] = useState<SelectListDto>();
  const [gradeId, setGradeId] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>();

  //#endregion

  //#region effects

  useEffect(() => {
    if (courseSelectList && courseSelectList.length > 0) {
      let courseSelectListItem;
      if (window.location.pathname.includes(CourseEnum.FIRST_COURSE)) {
        courseSelectListItem = courseSelectList.find(
          (c) => c.value.en.toLowerCase() === "first course"
        );
      } else if (window.location.pathname.includes(CourseEnum.SECOND_COURSE)) {
        courseSelectListItem = courseSelectList.find(
          (c) => c.value.en.toLowerCase() === "second course"
        );
      }
      if (courseSelectListItem) {
        setCourse(courseSelectListItem);
        document.title = `${t("documentTitle", {
          pageName:
            courseSelectListItem.value[i18n.language === "ar" ? "ar" : "en"],
        })}`;
      }
    }
  }, [courseSelectList]);

  useEffect(() => {
    dispatch(getCourseSelectList());
  }, []);

  useEffect(() => {
    if (course?.id) {
      dispatch(getGradeSelectListByCourse(course?.id));
    }
  }, [course]);

  useEffect(() => {
    const hash = history.location.hash;
    if (hash && grades && grades.length > 0) {
      let id = hash.replace("#", "");
      setActiveTab(id);
      setGradeId(id);
      dispatch(getPlans(course?.id as string, id));
    } else {
      if (grades && grades.length > 0) {
        setActiveTab(grades[0].id);
        setGradeId(grades[0].id);
        dispatch(getPlans(course?.id as string, grades[0].id));
      }
    }
  }, [grades]);

  useEffect(() => {
    if (gradeId) dispatch(getPlans(course?.id as string, gradeId));
  }, [gradeId]);

  useEffect(() => {
    if (!deleteLoading && deleteSuccess) {
      setDeletingId(null);
      setShowDeleteModal(false);
      dispatch(dispatch(getPlans(course?.id as string, gradeId as string)));
    }
  }, [deleteLoading]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    if (course && course.id && gradeId) {
      dispatch(toggleAddEditPlanModal());
      setShowAddEditModal(true);
    }
  };

  const onEdit = (id: string) => {
    if (course && course.id && gradeId) {
      setEditingId(id);
      setShowAddEditModal(true);
    }
  };

  const onToggleState = (id: string) => {
    if (course && course.id && gradeId) {
      dispatch(togglePlanState(id as string, history));
    }
  };

  const onDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteModal(true);
    dispatch(deletePlanConsent(id));
  };

  const onDeleteConfirm = () => {
    dispatch(deletePlan(deletingId as string, history));
    setDeletingId(null);
  };

  const toggleTab = (changingTab: any) => {
    if (activeTab !== changingTab) {
      setActiveTab(changingTab);
      setGradeId(changingTab);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("plans")}
            pageTitle={t(
              i18n.language === "ar"
                ? course?.value.ar || ""
                : course?.value.en || ""
            )}
          />
          {!loadingGrades && grades && grades.length > 0 && (
            <React.Fragment>
              <Row>
                <Col xxl={12}>
                  <Card>
                    <CardBody>
                      <Nav pills className="nav-success">
                        {grades?.map((grade: SelectListDto, index: number) => (
                          <NavItem key={index}>
                            <NavLink
                              href={`#${grade.id}`}
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: activeTab === grade.id,
                              })}
                              onClick={() => {
                                toggleTab(grade.id);
                              }}
                            >
                              {i18n.language === "ar"
                                ? grade.value.ar
                                : grade.value.en}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {profile && profile.role.key == RoleEnum.SUPER_ADMIN && (
                <Row className="g-2">
                  <Col className="col-sm-auto ms-auto">
                    <div className="list-grid-nav hstack gap-1">
                      {gradeId && course?.id && (
                        <Button
                          color="success"
                          onClick={() => {
                            onAdd();
                          }}
                        >
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", {
                            item: "plan",
                          })}
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="mt-2">
                <Col xxl={12}>
                  <TabContent activeTab={activeTab}>
                    {grades &&
                      grades.length > 0 &&
                      grades?.map((grade: SelectListDto, index: number) => (
                        <TabPane
                          tabId={grade.id}
                          id={`${grade.id}`}
                          key={index}
                        >
                          <React.Fragment>
                            {gradeId && (
                              <CardBody>
                                <Col lg={12}>
                                  <Row className="team-list grid-view-filter">
                                    {!loadingPlans &&
                                      plans &&
                                      plans.length > 0 &&
                                      plans.map((item: PlanDto, key) => (
                                        <Col key={key}>
                                          <Plan
                                            {...item}
                                            onEdit={() => onEdit(item.id)}
                                            onDelete={() => onDelete(item.id)}
                                            onToggleState={() =>
                                              onToggleState(item.id)
                                            }
                                          />
                                        </Col>
                                      ))}
                                    {loadingPlans && (
                                      <CardBody>
                                        <TableSpinner />
                                      </CardBody>
                                    )}
                                    {!loadingPlans &&
                                      (!plans || plans?.length === 0) && (
                                        <CardBody>
                                          <NoResultFound />
                                        </CardBody>
                                      )}
                                  </Row>
                                </Col>
                              </CardBody>
                            )}
                          </React.Fragment>
                        </TabPane>
                      ))}
                  </TabContent>
                </Col>
              </Row>
            </React.Fragment>
          )}
          {loadingGrades && <TableSpinner />}
          {!loadingGrades && (!grades || grades.length === 0) && (
            <NoResultFound />
          )}
        </Container>
      </div>
      {course?.id && gradeId && (
        <AddEditPlanModal
          courseId={course?.id as string}
          gradeId={gradeId as string}
          id={editingId}
          show={showAddEditModal}
          onCloseClick={() => {
            dispatch(toggleAddEditPlanModal());
            setEditingId(null);
            setShowAddEditModal(false);
            if (course?.id && gradeId) {
              dispatch(getPlans(course.id, gradeId));
            }
          }}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        show={showDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
        error={deleteError}
      />
    </React.Fragment>
  );
};
